import { QueryClient, QueryCacheNotifyEvent } from '@tanstack/react-query';
import { CubeQuery, CubeQueryRecord } from '../types/cubeQueryTypes';
import { QueryStore } from './QueryStore';
import { TabStateManager } from './TabStateManager';
import { deriveContextFromQueryKey, deriveContextFromQuery } from './componentNameExtractor';

function isCubeQuery(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const obj = value as Record<string, unknown>;
  return (
    Array.isArray(obj.measures) ||
    Array.isArray(obj.dimensions) ||
    Array.isArray(obj.segments) ||
    Array.isArray(obj.timeDimensions)
  );
}

function findCubeQueryInKey(queryKey: ReadonlyArray<unknown>): CubeQuery | undefined {
  for (const part of queryKey) {
    if (isCubeQuery(part)) {
      return part as CubeQuery;
    }
  }
  return undefined;
}

const getCurrentPagePath = (fallbackPath: string): string => {
  if (typeof window === 'undefined') return fallbackPath;
  return window.location.pathname;
};

export class CacheSubscriber {
  private queryClient: QueryClient | null = null;

  private unsubscribe: (() => void) | null = null;

  private capturedQueryHashes = new Set<string>();

  private currentPagePath = '/';

  constructor(
    private queryStore: QueryStore,
    private tabStateManager: TabStateManager,
    private getCubejsApiUrl: () => string,
    private isRecentNetworkQuery: (hash: string) => boolean,
    private generateRequestId: () => string,
  ) {}

  setQueryClient(client: QueryClient): void {
    this.queryClient = client;
  }

  startListening(): void {
    if (!this.queryClient || this.unsubscribe) return;

    const cache = this.queryClient.getQueryCache();
    this.unsubscribe = cache.subscribe(this.handleCacheEvent);
  }

  stopListening(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  updatePagePath(pagePath: string): void {
    this.currentPagePath = pagePath;
    this.capturedQueryHashes.clear();
  }

  clearCapturedHashes(): void {
    this.capturedQueryHashes.clear();
  }

  private handleCacheEvent = (event: QueryCacheNotifyEvent): void => {
    if (!this.queryStore.getRecording()) return;

    if (event.type !== 'updated' && event.type !== 'observerResultsUpdated' && event.type !== 'observerAdded') return;

    const { query } = event;

    if (!Array.isArray(query.queryKey)) return;

    const cubeQuery = findCubeQueryInKey(query.queryKey);
    if (!cubeQuery) return;

    if (query.getObserversCount() === 0) return;

    const queryHash = JSON.stringify(cubeQuery);
    const pagePath = getCurrentPagePath(this.currentPagePath);
    const capturedQueryKey = `${pagePath}:${queryHash}`;

    if (this.capturedQueryHashes.has(capturedQueryKey)) return;
    if (this.isRecentNetworkQuery(queryHash)) return;

    if (!query.state.dataUpdatedAt) return;

    this.capturedQueryHashes.add(capturedQueryKey);

    const componentName =
      deriveContextFromQueryKey(query.queryKey) || deriveContextFromQuery(cubeQuery) || 'CachedQuery';

    const record: CubeQueryRecord = {
      id: this.generateRequestId(),
      timestamp: Date.now(),
      componentName,
      pagePath,
      tabName: this.tabStateManager.getActiveTab() || undefined,
      query: cubeQuery,
      queryKey: query.queryKey.filter((key): key is string => typeof key === 'string'),
      url: this.getCubejsApiUrl(),
      method: 'CUBE_LOAD',
      duration: 0,
      status: 200,
      dataSource: 'cache',
      cached: true,
      dataUpdatedAt: query.state.dataUpdatedAt,
    };

    if (!this.queryStore.isFull()) {
      this.queryStore.addQuery(record);
    }
  };
}
